/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { inject, Injectable, NgZone, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { take } from 'rxjs/operators';
import {
  ANALYTICS_COOKIES,
  COOKIE_CONSENT_DECISION_COOKIE,
  EMBEDDED_CONTENT_CATEGORY,
  embeddedContentDecisionFromCookie,
  hasConsentDecision,
  NECESSARY_COOKIES,
  OPTIONAL_CATEGORY_DEFAULT_ON,
  unrecordedCategories,
} from './cookie-names';

interface CookieControlPlugin {
  load: (config: unknown) => void;
  open?: () => void;
  hide?: () => void;
  notify?: () => void;
  changeCategory?: (index: number, on: boolean) => void;
}

declare global {
  interface Window {
    CookieControl?: CookieControlPlugin;
  }
}

export { ANALYTICS_COOKIES, COOKIE_CONSENT_DECISION_COOKIE, EMBEDDED_CONTENT_CATEGORY, NECESSARY_COOKIES } from './cookie-names';

const COOKIE_POLICY_URL = 'https://www.potential.ly/privacy#cookies';
const POLICY_UPDATED = '22/09/2026';
const PLUGIN_POLL_INTERVAL_MS = 250;
const PLUGIN_POLL_ATTEMPTS = 40;
const SETTINGS_PANEL_CLOSE_BUTTON = '#ccc-close';
const SETTINGS_PANEL = '#ccc-module';
const SETTINGS_PANEL_CATEGORIES = '#ccc-optional-categories';

@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  private readonly transloco = inject(TranslocoService);
  private readonly ngZone = inject(NgZone);

  private loadRequested = false;
  private pluginLoaded = false;
  private arrivedUndecided = false;

  /**
   * Whether third-party embeds may be put on the page: Iframely previews and players for any link an author
   * adds, whatever site it points to, plus the Google Maps view on events. On unless the visitor switches the
   * category off, and declared under legitimate interest with an opt-out.
   */
  readonly embeddedContentAllowed = signal(true);

  load(): void {
    if (this.loadRequested) {
      return;
    }
    this.loadRequested = true;
    this.arrivedUndecided = this.hasConsentCookie() && !this.hasDecision();
    this.syncEmbeddedContentFromStoredDecision();
    this.transloco
      .load(this.transloco.getActiveLang())
      .pipe(take(1))
      .subscribe({
        next: () => this.awaitPlugin(0),
        error: () => this.awaitPlugin(0),
      });
  }

  open(): void {
    window.CookieControl?.open?.();
  }

  /**
   * For LTI and other iframe launches, where the app runs inside an institution's page that presents its own
   * consent and the banner is never shown here. Embeds stay allowed, as the host page decides.
   */
  deferToHostPage(): void {
    this.embeddedContentAllowed.set(true);
  }

  get available(): boolean {
    return this.pluginLoaded && typeof window.CookieControl?.open === 'function';
  }

  hasDecision(): boolean {
    return hasConsentDecision(document.cookie);
  }

  private awaitPlugin(attempt: number): void {
    if (window.CookieControl) {
      this.ngZone.run(() => {
        window.CookieControl.load(this.buildConfig());
        this.pluginLoaded = true;
      });
      this.askAgainWhenSettingsCloseUndecided();
      return;
    }
    if (attempt >= PLUGIN_POLL_ATTEMPTS) {
      return;
    }
    this.ngZone.runOutsideAngular(() => setTimeout(() => this.awaitPlugin(attempt + 1), PLUGIN_POLL_INTERVAL_MS));
  }

  /**
   * The plugin closes its settings panel on the X or Escape without a choice, and stops showing the banner
   * from then on. Until the visitor accepts, rejects or sets a category, the banner comes back instead.
   */
  private askAgainWhenSettingsCloseUndecided(): void {
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener(
        'change',
        (event) => {
          if ((event.target as Element | null)?.closest?.(SETTINGS_PANEL_CATEGORIES)) {
            setTimeout(() => this.recordUnchangedCategories());
          }
        },
        true,
      );
      document.addEventListener(
        'click',
        (event) => {
          if ((event.target as Element | null)?.closest?.(SETTINGS_PANEL_CLOSE_BUTTON)) {
            setTimeout(() => this.askAgainIfUndecided());
          }
        },
        true,
      );
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape' && document.querySelector(SETTINGS_PANEL)) {
            setTimeout(() => this.askAgainIfUndecided());
          }
        },
        true,
      );
    });
  }

  /**
   * Stores the categories the visitor left alone in the state they were shown in. Set explicitly rather than
   * toggled: the plugin toggles from the stored value, so a category without one would end up revoked.
   */
  private recordUnchangedCategories(): void {
    unrecordedCategories(document.cookie).forEach(({ index, defaultOn }) => {
      window.CookieControl?.changeCategory?.(index, defaultOn);
    });
  }

  /** A first visit has no consent cookie, and the plugin shows the banner on its own. */
  private askAgainIfUndecided(): void {
    if (this.hasConsentCookie() && !this.hasDecision() && !document.querySelector(SETTINGS_PANEL)) {
      window.CookieControl?.notify?.();
    }
  }

  private hasConsentCookie(): boolean {
    return document.cookie.split('; ').some((entry) => entry.startsWith(`${COOKIE_CONSENT_DECISION_COOKIE}=`));
  }

  private setEmbeddedContentAllowed(allowed: boolean): void {
    this.ngZone.run(() => this.embeddedContentAllowed.set(allowed));
  }

  private syncEmbeddedContentFromStoredDecision(): void {
    this.setEmbeddedContentAllowed(embeddedContentDecisionFromCookie(document.cookie) !== false);
  }

  private buildConfig(): unknown {
    const t = (key: string) => this.transloco.translate(`translations.cookieConsent.${key}`);

    return {
      apiKey: '7d639679919fe751bb087c529e486d9b761df8b6',
      product: 'PRO',
      initialState: 'notify',
      mode: 'gdpr',
      acceptBehaviour: 'all',
      notifyDismissButton: false,
      rejectButton: true,
      closeOnGlobalChange: true,
      consentCookieExpiry: 90,
      subDomains: true,
      sameSiteCookie: true,
      sameSiteValue: 'Lax',
      onLoad: () => this.arrivedUndecided && this.askAgainIfUndecided(),
      branding: {
        removeIcon: false,
      },
      accessibility: {
        outline: true,
        highlightFocus: true,
      },
      statement: {
        name: t('statementName'),
        description: t('statementDescription'),
        url: COOKIE_POLICY_URL,
        updated: POLICY_UPDATED,
      },
      necessaryCookies: NECESSARY_COOKIES,
      optionalCookies: [
        {
          name: 'analytics',
          label: t('analytics.label'),
          description: t('analytics.description'),
          cookies: ANALYTICS_COOKIES,
          recommendedState: OPTIONAL_CATEGORY_DEFAULT_ON.analytics,
          initialConsentState: OPTIONAL_CATEGORY_DEFAULT_ON.analytics,
          vendors: [
            {
              name: 'Google Analytics',
              description: t('analytics.vendorGoogle'),
              url: 'https://policies.google.com/technologies/cookies',
            },
          ],
          thirdPartyCookies: [{ name: 'Google Analytics', optOutLink: 'https://tools.google.com/dlpage/gaoptout' }],
        },
        {
          name: EMBEDDED_CONTENT_CATEGORY,
          label: t('embeddedContent.label'),
          description: t('embeddedContent.description'),
          cookies: [],
          lawfulBasis: 'legitimate interest',
          recommendedState: OPTIONAL_CATEGORY_DEFAULT_ON[EMBEDDED_CONTENT_CATEGORY],
          initialConsentState: OPTIONAL_CATEGORY_DEFAULT_ON[EMBEDDED_CONTENT_CATEGORY],
          vendors: [
            {
              name: 'Iframely',
              description: t('embeddedContent.vendorIframely'),
              url: 'https://iframely.com/privacy',
            },
            {
              name: 'YouTube (Google)',
              description: t('embeddedContent.vendorMediaHosts'),
              url: 'https://policies.google.com/technologies/cookies',
            },
            {
              name: 'Google Maps',
              description: t('embeddedContent.vendorMaps'),
              url: 'https://policies.google.com/technologies/cookies',
            },
            {
              name: t('embeddedContent.vendorAnySiteName'),
              description: t('embeddedContent.vendorAnySite'),
              url: COOKIE_POLICY_URL,
            },
          ],
          onAccept: () => this.setEmbeddedContentAllowed(true),
          onRevoke: () => this.setEmbeddedContentAllowed(false),
        },
      ],
      text: {
        title: t('title'),
        intro: t('intro'),
        necessaryTitle: t('necessaryTitle'),
        necessaryDescription: t('necessaryDescription'),
        thirdPartyTitle: t('thirdPartyTitle'),
        thirdPartyDescription: t('thirdPartyDescription'),
        thirdPartyCookies: t('thirdPartyCookies'),
        notifyTitle: t('notifyTitle'),
        notifyDescription: t('notifyDescription'),
        on: t('on'),
        off: t('off'),
        accept: t('accept'),
        reject: t('reject'),
        settings: t('settings'),
        acceptRecommended: t('acceptRecommended'),
        acceptSettings: t('accept'),
        rejectSettings: t('reject'),
        readMore: t('readMore'),
      },
    };
  }
}
